<template>
    <wrapper-features-templates-operations
    :showLoader="showLoader"
    :showOverlayLoader="showOverlayLoader"
    :applyErrorState="applyErrorState && !showLoader"
    :feedbackOperationData="feedbackOperationData"
    :activeStep="activeStep"
    :scrollValue="scrollValue"
    :stepperParams="stepperParams"
    :showStepper="activeStep <= stepperParams.length && !applyErrorState && !showLoader && getCardsList.length"
    :showFooter="activeStep < 3 && !showLoader && !applyErrorState && getCardsList.length"
    :footerButtonsParams="footerButtonsParams.filter(obj => obj.step === activeStep)"
    :tokenParams="{secretCode: formData.secretCode}"
    :tokenTitleAppend="t('cards.cardlessWithdraw.generateCode.tokenTitleAppend')"
    @challengeSuccessHandler="successHandler"
    @challengeErrorHandler="errorHandler($event, requestCreateCardlessWithdraw)"
    @clearScrollToValue="scrollValue = null"
    @backOrCloseClicked="emit('backOrCloseClicked')">
        <!-- Insert content -->
        <component
        v-if="getCardsList.length && !showLoader && !applyErrorState"
        :is="getActiveStepComponent"
        :getCardsList="getCardsList"
        :getAmountsList="getAmountsList"
        :clearFormErrors="clearFormErrors"
        :formData="formData"
        :mandatoryList="mandatoryList"
        :responseData="responseData"
        :showMandatoryErrors="showMandatoryErrors"
        @newTransfer="clearInputs()"
        @updateFormData="updateFormDataHandler"
        @openReusePaymentModal="showReuseTransferModal = true"
        @formErrorsCleared="clearFormErrors = false"
        @resetShowMandatoryErrorsProp="showMandatoryErrors = false"
        @scrollTo="scrollValue = $event"
        @showOverlayLoader="showOverlayLoader = true"
        @hideOverlayLoader="showOverlayLoader = false"/>
        <!-- Empty-state -->
        <template #empty-state-slot>
            <empty-state-generic
            v-if="!showLoader && !applyErrorState && !getCardsList.length"
            illustration="emptyState/cards-withdraw"
            :title="t('cards.cardlessWithdraw.generateCode.emptyStateTitle')"
            :description="t('cards.cardlessWithdraw.generateCode.emptyStateDescription')"/>
        </template>
    </wrapper-features-templates-operations>
    <!-- Feedback Toasts -->
    <toast-generic
	v-if="showFeedbackToast"
	:toastType="feedbackToastData.toastType"
	:feedbackText="feedbackToastData.text"
	:buttonsParams="feedbackToastData.buttons"
	@hideMe="showFeedbackToast = false"/>
    <reuse-modal
    v-if="showReuseTransferModal"
    :getCardsList="getCardsList"
    @reuseTransferRowClicked="setTransferDataFromReuseModal"
    @hideMe="showReuseTransferModal = false"/>
</template>

<script setup>
import WrapperFeaturesTemplatesOperations from '@/components/wrappers/featuresTemplates/Operations'
import EmptyStateGeneric from '@/components/generic/emptyStates/Generic'
import ToastGeneric from '@/components/generic/toasts/Generic'
import ReuseModal from '@/components/features/cards/cardlessWithdraw/generateCode/ReuseModal'

/* Vue */
import { ref, computed, defineAsyncComponent, onBeforeUnmount, watch } from 'vue'

/* Assets & Helpers */
import { atLeastOneIsEmpty, clearObjectStrings } from '@/assets/scripts/objects'
import { errorObjectConstructor } from '@/assets/scripts/toastsHelpers'

/* Store */
import { mapActions, mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/** Props
@params { Object } - Object with favorite info
*/
const props = defineProps({
    params: Object
})

/* Emmitters */
const emit = defineEmits(['backOrCloseClicked'])

/* mapActions & mapGetters */
const { requestCards, requestAmountsList, createCardlessWithdraw, clearFeatureStates } = mapActions(moduleMapper.CARDS_CARDLESS_WITHDRAW_GENERATE_CODE)
const {	getCardsList, getAmountsList } = mapGetters(moduleMapper.CARDS_CARDLESS_WITHDRAW_GENERATE_CODE)

/* Data */
const showLoader = ref(false)
const showOverlayLoader = ref(false)
const applyErrorState = ref(false)
const scrollValue = ref(null)
const showMandatoryErrors = ref(false)
const clearFormErrors = ref(false)
const showReuseTransferModal = ref(false)

/*************************************************
*                                                *
*                    Steps Data                  *
*                                                *
*************************************************/
const activeStep = ref(1)
const stepperParams = ref([
    { description: t('cards.cardlessWithdraw.generateCode.stepperParams.data') },
    { description: t('cards.cardlessWithdraw.generateCode.stepperParams.validation') }
])

const goToStep = stepNumber => {
    activeStep.value = stepNumber
    scrollValue.value = 0
}

const getActiveStepComponent = computed(() => {
    const stepNumber = activeStep.value
    return defineAsyncComponent(() => import('@/components/features/cards/cardlessWithdraw/generateCode/Step' + stepNumber))
})

/*************************************************
*                                                *
*                    Form Data                   *
*                                                *
*************************************************/
const formData = ref({
    originCard: '',
    currency: '',
    personTabId: 1,
    otherMsisdn: '',
    amountCode: '',
    secretCode: '',
    repeatSecretCode: ''
})

const mandatoryList = ref([
    'originCard',
    'amountCode',
    'secretCode',
    'repeatSecretCode'
])

const updateFormDataHandler = ({ field, inputValue }) => {
    formData.value[field] = inputValue

    if (field === 'personTabId' && inputValue === 2) formData.value.otherMsisdn = ''
}

/*************************************************
*                                                *
*           Reuse Transfer Modal Logic           *
*                                                *
*************************************************/
const setTransferDataFromReuseModal = (ev) => {
    formData.value.originCard = ev.originCard
    formData.value.amountCode = ev.amountCode

    if (ev.destinationMsisdn.length && ev.destinationMsisdn !== '-') {
        formData.value.otherMsisdn = ev.destinationMsisdn
    } else formData.value.personTabId = 2
    formData.value.currency = ev.currency
}

const getParameterValue = (paramsList, paramName) => {
    const targetParam = paramsList.find(param => param.name === paramName)

    return targetParam && targetParam.value ? targetParam.value : ''
}

const setDataFromFavoritePropsParams = params => {
    const favoriteInfo = params.customerOperationView.parameters

    formData.value.originCard = getParameterValue(favoriteInfo, 'originCard')
    formData.value.amountCode = getParameterValue(favoriteInfo, 'amountCode')
    formData.value.currency = getParameterValue(favoriteInfo, 'currency')

    const destinationMsisdn = getParameterValue(favoriteInfo, 'destinationMsisdn')

    if (destinationMsisdn !== '' && destinationMsisdn !== '-') {
        formData.value.otherMsisdn = destinationMsisdn
    } else formData.value.personTabId = 2
}
/*************************************************
*                                                *
*                 Requests Logic                 *
*                                                *
*************************************************/
const initialRequests = async () => {
	showLoader.value = true
    applyErrorState.value = false

	try {
        await requestCards()
        await requestAmountsList()
        if (getCardsList.value.length) {
            formData.value.originCard = getCardsList.value[0].id
            formData.value.currency = getCardsList.value[0].currency
        }

        if (props.params) setDataFromFavoritePropsParams(props.params)

		showLoader.value = false
	} catch (error) {
        if (error.toString() === 'Cancel') return
        applyErrorState.value = true
        errorHandler(error.response.data, initialRequests)
	}
}

const requestCreateCardlessWithdraw = async () => {
    showOverlayLoader.value = true

    const payload = {
        data: {
            originCard: formData.value.originCard,
            amountCode: formData.value.amountCode,
            amount: getAmountsList.value.find(amount => amount.code === formData.value.amountCode).amount,
            currency: formData.value.currency,
            secretCode: formData.value.secretCode
        }
    }

    if (formData.value.personTabId !== 2) payload.data.destinationMsisdn = formData.value.otherMsisdn

    try {
        await createCardlessWithdraw(payload)
        showOverlayLoader.value = false
    } catch (error) {
        if (error.toString() === 'Cancel') return
        errorHandler(error.response.data, requestCreateCardlessWithdraw)
    }
}

/*************************************************
*                                                *
*             Success/Error Handlers             *
*                                                *
*************************************************/
const responseData = ref(null)
const showFeedbackToast = ref(false)
const feedbackToastData = ref({
	toastType: '',
	text: '',
	buttons: []
})
const feedbackOperationData = ref(null)

const successHandler = (data) => {
    responseData.value = {
        ...data.data,
        errorLevel: data.errorLevel,
        code: data.code
    }

    feedbackOperationData.value = {
        status: data.errorLevel,
        title: data.message
    }
    goToStep(3)
}

const errorHandler = (data, retryFn) => {
	feedbackToastData.value = errorObjectConstructor(data, retryFn, () => { activeStep.value = 3 })

    showLoader.value = false
    showOverlayLoader.value = false
	showFeedbackToast.value = true
}
/*************************************************
*                                                *
*              Footer Buttons Data               *
*                                                *
*************************************************/
const footerButtonsParams = computed(() => {
    return [{
        label: t('cards.cardlessWithdraw.generateCode.footerButtons.clear'),
        callbackFunction: clearInputs,
        buttonPosition: 'left',
        step: 1
    }, {
        label: t('cards.cardlessWithdraw.generateCode.footerButtons.forward'),
        callbackFunction: validateInputs,
        icon: 'arrow-right-full',
        iconPosition: 'right',
        buttonType: 'primary',
        step: 1
    }, {
        label: t('cards.cardlessWithdraw.generateCode.footerButtons.edit'),
        icon: 'arrow-left-full',
        callbackFunction: () => { activeStep.value-- },
        step: 2
    },
    {
        label: t('cards.cardlessWithdraw.generateCode.footerButtons.forward'),
        callbackFunction: requestCreateCardlessWithdraw,
        icon: 'arrow-right-full',
        iconPosition: 'right',
        buttonType: 'primary',
        step: 2
    }]
})

const validateInputs = () => {
    if (formData.value.personTabId === 1 && !formData.value.otherMsisdn.length) {
        mandatoryList.value.push('otherMsisdn')
    } else if (formData.value.personTabId === 2 && formData.value.otherMsisdn.length) {
        mandatoryList.value = ['originCard', 'amountCode', 'secretCode', 'repeatSecretCode']
    } else if (formData.value.personTabId === 2) {
        mandatoryList.value = ['originCard', 'amountCode', 'secretCode', 'repeatSecretCode']
    }

    (formData.value.secretCode !== formData.value.repeatSecretCode) || (formData.value.personTabId === 1 && formData.value.otherMsisdn.length < 9) || formData.value.secretCode.length < 3 || atLeastOneIsEmpty(formData.value, mandatoryList.value) ? showMandatoryErrors.value = true : goToStep(2)
}

const clearInputs = () => {
    clearFormErrors.value = true
    clearObjectStrings(formData.value)

    /* Setting Initial Selected Card and Currency */
    formData.value.originCard = getCardsList.value[0].id
    formData.value.currency = getCardsList.value[0].currency
    formData.value.personTabId = 1

    feedbackOperationData.value = null
    activeStep.value = 1
    responseData.value = null
}

watch(() => activeStep.value, (newVal, oldVal) => {
    scrollValue.value = 0
})

initialRequests()

onBeforeUnmount(() => {
    clearFeatureStates()
})
</script>

<style lang="scss" scoped>
.button-primary {
    min-width: 200px;

    &.mobile {
        width: 100%;
    }
}
</style>
